import { JobStatusEnum, JobType } from '../../__generated__/globalTypes';
import { GetCompanyJobs_companyJobs_jobs } from '../../query/__generated__/GetCompanyJobs';

// Type alias for convenience
export type Job = GetCompanyJobs_companyJobs_jobs;

// Polling intervals
export const ACTIVE_POLL_INTERVAL = 3000; // 3s when jobs are active
export const BACKGROUND_POLL_INTERVAL = 30000; // 30s background check for new jobs

// ETA calculation constants
const MIN_PROGRESS_FOR_ETA = 5; // Don't show ETA until at least 5% progress
const MIN_ELAPSED_MS_FOR_ETA = 5000; // Don't show ETA until 5 seconds have elapsed
const MAX_ETA_DISPLAY_HOURS = 24; // Cap ETA display at 24 hours

/**
 * Formats remaining time into a human-readable string.
 * Returns null if the time cannot be reasonably displayed.
 */
export function formatRemainingTime(remainingMs: number): string | null {
  if (remainingMs <= 0) {
    return null;
  }

  const seconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  // Cap at max displayable time
  if (hours >= MAX_ETA_DISPLAY_HOURS) {
    return `> ${MAX_ETA_DISPLAY_HOURS} hours`;
  }

  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    if (remainingMinutes > 0) {
      return `~${hours}h ${remainingMinutes}m remaining`;
    }
    return `~${hours} hour${hours !== 1 ? 's' : ''} remaining`;
  }

  if (minutes > 0) {
    return `~${minutes} min${minutes !== 1 ? 's' : ''} remaining`;
  }

  if (seconds > 10) {
    return `~${seconds} sec remaining`;
  }

  // Very short time remaining
  return 'Almost done';
}

/**
 * Calculates the estimated time remaining for a job.
 * Returns a formatted string or null if ETA cannot be calculated reliably.
 *
 * Production considerations:
 * - Requires minimum progress (5%) to avoid wildly inaccurate early estimates
 * - Requires minimum elapsed time (5s) to have meaningful data
 * - Uses processed_count/estimated_count for more accurate rate calculation
 * - Gracefully handles edge cases (null started_at, 0 progress, etc.)
 *
 * @param job - The job to calculate ETA for
 * @param now - Current timestamp in ms (defaults to Date.now(), injectable for testing)
 */
export function calculateETA(job: Job, now: number = Date.now()): string | null {
  // Can't calculate ETA for pending jobs or if started_at is missing
  if (job.status !== JobStatusEnum.IN_PROGRESS || job.started_at == null) {
    return null;
  }

  const startedAt = Number(job.started_at);

  // Guard against NaN if started_at is not a valid number (e.g., ISO date string)
  if (Number.isNaN(startedAt)) {
    return 'Calculating...';
  }

  const elapsedMs = now - startedAt;

  // Need minimum elapsed time for reliable estimate
  if (elapsedMs < MIN_ELAPSED_MS_FOR_ETA) {
    return 'Calculating...';
  }

  // Need minimum progress for reliable estimate
  if (job.progress_percentage < MIN_PROGRESS_FOR_ETA) {
    return 'Calculating...';
  }

  // Use item counts for more accurate rate calculation when available
  const processedCount = job.processed_count;
  const estimatedCount = job.estimated_count;

  if (estimatedCount > 0 && processedCount > 0) {
    // Calculate rate based on items processed
    const itemsPerMs = processedCount / elapsedMs;
    const remainingItems = estimatedCount - processedCount;

    if (itemsPerMs > 0 && remainingItems > 0) {
      const remainingMs = remainingItems / itemsPerMs;
      return formatRemainingTime(remainingMs);
    }
  }

  // Fallback to percentage-based calculation
  const progress = job.progress_percentage;
  if (progress > 0 && progress < 100) {
    const remainingProgress = 100 - progress;
    const msPerPercent = elapsedMs / progress;
    const remainingMs = remainingProgress * msPerPercent;
    return formatRemainingTime(remainingMs);
  }

  return null;
}

/** Returns an emoji icon for the given job type */
export function getJobIcon(jobType: JobType): string {
  switch (jobType) {
    case JobType.POOL_CREATION:
      return '📊';
    case JobType.OFFERING_CREATION:
      return '📤';
    default:
      return '⚙️';
  }
}

/** Returns a human-readable label for the given job */
export function getJobLabel(job: Job): string {
  switch (job.job_type) {
    case JobType.POOL_CREATION:
      return 'pool_name' in job && job.pool_name ? `Creating "${job.pool_name}"` : 'Creating Pool';
    case JobType.OFFERING_CREATION:
      return 'offering_name' in job && job.offering_name
        ? `Creating Offering: ${job.offering_name}`
        : 'Creating Offering';
    default:
      return 'Processing';
  }
}

/** Returns a Tailwind text color class for the given job status */
export function getStatusColor(status: JobStatusEnum): string {
  switch (status) {
    case JobStatusEnum.IN_PROGRESS:
      return 'text-violet-400';
    case JobStatusEnum.PENDING:
    default:
      return 'text-gray-500';
  }
}
